import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import {
  DEFERRED_RETRY_MS,
  pageWouldInterrupt,
  PwaUpdateSession,
  RELOAD_FALLBACK_MS,
  reloadPage,
  UPDATE_CHECK_INTERVAL_MS,
} from "./pwaUpdateSession";

/**
 * Applies a newly deployed build automatically, with no prompt.
 *
 * The portal ships often during beta and the service worker precaches the app
 * shell, so a long-open tab (or the installed app) keeps running yesterday's
 * bundle until something replaces it. That is how already-fixed bugs got
 * re-reported.
 *
 * The reload belongs to us. While pageWouldInterrupt() is true (open modal,
 * unsaved admin edits, a filled textarea, or typing since the last navigation)
 * the update is held and retried. Other tabs hear that hold over
 * BroadcastChannel, so activating the worker reloads an idle tab and leaves a
 * tab with unsaved work alone until that work is gone.
 */

function serviceWorkerUrl(): string {
  const base = import.meta.env.BASE_URL || "/";
  return `${base.endsWith("/") ? base : `${base}/`}sw.js`;
}

/**
 * Tell the waiting worker to skip waiting, then tell the session once it has
 * activated. The fallback covers a worker that never reports the state change.
 * Both paths re-enter the session, which runs the busy gate again.
 */
function armSkipWaiting(
  getRegistration: () => ServiceWorkerRegistration | null,
  onActivated: () => void,
): { skipWaiting: () => void; cancel: () => void } {
  let alive = true;
  let timer = 0;
  let worker: ServiceWorker | null = null;
  let settled = false;

  const finish = () => {
    if (!alive || settled) return;
    settled = true;
    if (timer) window.clearTimeout(timer);
    timer = 0;
    worker?.removeEventListener("statechange", onState);
    onActivated();
  };

  const onState = () => {
    if (worker && (worker.state === "activated" || worker.state === "redundant")) finish();
  };

  return {
    cancel() {
      alive = false;
      if (timer) window.clearTimeout(timer);
      timer = 0;
      worker?.removeEventListener("statechange", onState);
    },
    skipWaiting() {
      if (!alive || settled) return;
      const waiting = getRegistration()?.waiting ?? null;
      if (!waiting) {
        finish();
        return;
      }
      worker = waiting;
      waiting.addEventListener("statechange", onState);
      timer = window.setTimeout(finish, RELOAD_FALLBACK_MS);
      waiting.postMessage({ type: "SKIP_WAITING" });
    },
  };
}

function noteWorker(reg: ServiceWorkerRegistration, session: PwaUpdateSession): void {
  const controller = navigator.serviceWorker.controller;
  // A hard refresh leaves controller null while registration.active is the
  // previous worker, so a waiting worker is still an update. First install
  // has neither a controller nor an active worker, and must not reload.
  if (reg.waiting && (controller || reg.active)) {
    session.onUpdateAvailable();
    return;
  }
  if (reg.active && controller && reg.active !== controller) session.onWorkerActivated();
}

export function PwaAutoUpdate() {
  const { pathname } = useLocation();
  // True once the user has typed into anything since the last navigation.
  // Covers the full-page editors a modal check misses: the form runner, the
  // KB editor, the form builder, the status alert composer.
  const typedSinceNavigation = useRef(false);
  const sessionRef = useRef<PwaUpdateSession | null>(null);

  useEffect(() => {
    const onInput = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        typedSinceNavigation.current = true;
      }
    };
    document.addEventListener("input", onInput, true);
    return () => document.removeEventListener("input", onInput, true);
  }, []);

  // Declared before the session's pathname retry so a navigation clears the
  // flag first. Effects run in definition order.
  useEffect(() => {
    typedSinceNavigation.current = false;
  }, [pathname]);

  useEffect(() => {
    sessionRef.current?.recheck();
  }, [pathname]);

  useEffect(() => {
    // devOptions.enabled is false, so the dev server has no worker to register.
    if (import.meta.env.MODE === "development") return;
    if (!("serviceWorker" in navigator)) return;

    let registration: ServiceWorkerRegistration | null = null;
    let hadController = false;
    let stopped = false;
    let session: PwaUpdateSession | null = null;
    let activation: { cancel: () => void } | null = null;
    let listening = false;
    let updateTimer = 0;
    let retryTimer = 0;
    let queued = 0;
    let observer: MutationObserver | null = null;
    let onControllerChange: (() => void) | null = null;
    let onVisibility: (() => void) | null = null;
    let onLoad: (() => void) | null = null;

    const cleanup = () => {
      stopped = true;
      activation?.cancel();
      session?.stop();
      sessionRef.current = null;
      if (listening && onControllerChange) {
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
      }
      if (onLoad) window.removeEventListener("load", onLoad);
      onLoad = null;
      if (updateTimer) window.clearInterval(updateTimer);
      if (retryTimer) window.clearInterval(retryTimer);
      if (queued) window.clearTimeout(queued);
      if (onVisibility) document.removeEventListener("visibilitychange", onVisibility);
      observer?.disconnect();
    };

    try {
      hadController = Boolean(navigator.serviceWorker.controller);
      const armed = armSkipWaiting(
        () => registration,
        () => session?.onWorkerActivated(),
      );
      activation = armed;
      session = new PwaUpdateSession({
        isBusy: () => pageWouldInterrupt(typedSinceNavigation.current),
        reload: () => reloadPage(),
        skipWaiting: () => armed.skipWaiting(),
        onExternalActivation: () => {
          if (registration && session) noteWorker(registration, session);
        },
      });
      sessionRef.current = session;

      onControllerChange = () => {
        if (!session) return;
        const had = hadController;
        hadController = Boolean(navigator.serviceWorker.controller);
        if (registration) noteWorker(registration, session);
        session.onControllerChange(had);
      };

      session.start();
      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
      listening = true;

      const watch = (reg: ServiceWorkerRegistration) => {
        if (!session) return;
        noteWorker(reg, session);
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          installing?.addEventListener("statechange", () => {
            if (installing.state === "installed" && session) noteWorker(reg, session);
          });
        });
      };

      const check = () => {
        if (!registration || !session) return;
        void registration
          .update()
          .then(() => {
            if (registration && session) noteWorker(registration, session);
          })
          .catch(() => undefined);
      };

      // noteWorker covers a tab that missed the broadcast: after another tab
      // activates the worker, registration.active and this page's controller differ.
      const refresh = () => {
        if (!session) return;
        if (registration) noteWorker(registration, session);
        session.recheck();
      };

      // The plugin registered after window.load so the first-visit precache
      // did not compete with first paint. Same here. A document that is
      // already complete (tests, or a late mount) registers immediately.
      const registerWorker = () => {
        void navigator.serviceWorker
          .register(serviceWorkerUrl(), { scope: import.meta.env.BASE_URL || "/" })
          .then((reg) => {
            if (stopped || !session) return;
            registration = reg;
            watch(reg);
          })
          .catch(() => undefined);
      };
      if (document.readyState === "complete") {
        registerWorker();
      } else {
        onLoad = () => {
          registerWorker();
        };
        window.addEventListener("load", onLoad, { once: true });
      }

      updateTimer = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
      retryTimer = window.setInterval(refresh, DEFERRED_RETRY_MS);
      onVisibility = () => {
        if (document.visibilityState !== "visible") return;
        check();
        refresh();
      };
      document.addEventListener("visibilitychange", onVisibility);

      // Modal close removes a backdrop node; an admin section drops `dirty`.
      // Coalesce bursts of DOM updates so a React render does not scan the page
      // on every commit. 200ms is still well inside the 30s retry.
      const scheduleRefresh = () => {
        if (queued) return;
        queued = window.setTimeout(() => {
          queued = 0;
          refresh();
        }, 200);
      };
      observer = new MutationObserver(scheduleRefresh);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });
    } catch (error) {
      console.warn("PWA update setup failed", error);
      cleanup();
    }

    return cleanup;
  }, []);

  return null;
}
